import React, { useEffect, useState } from "react";
import { Download, Loader2, FileText, Eye, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PdfBlobViewer } from "./PdfBlobViewer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const EVT = "gr-apri-file";
const EVT_MAIL = "gr-mail-file";
export const apriFile = (url, titolo, params) => window.dispatchEvent(new CustomEvent(EVT, { detail: { url, titolo, params } }));
export const inviaFile = (url, titolo, params) => window.dispatchEvent(new CustomEvent(EVT_MAIL, { detail: { url, titolo, params } }));
export const scaricaFile = async (url, titolo, params) => {
  try {
    const res = await api.get(url, { params, responseType: "blob" });
    const obj = URL.createObjectURL(res.data); const a = document.createElement("a");
    a.href = obj; a.download = titolo || "documento"; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(obj), 60000);
  } catch { toast.error("Errore download"); }
};

export const FileAzioni = ({ url, titolo, params, testid, size = 15 }) => (
  <span className="inline-flex items-center gap-0.5 shrink-0" data-testid={`file-azioni-${testid}`}>
    <button type="button" onClick={() => apriFile(url, titolo, params)} title="Anteprima" className="p-1.5 rounded hover:bg-secondary text-teal-700 transition-colors" data-testid={`file-anteprima-${testid}`}><Eye size={size} /></button>
    <button type="button" onClick={() => scaricaFile(url, titolo, params)} title="Scarica" className="p-1.5 rounded hover:bg-secondary text-emerald-700 transition-colors" data-testid={`file-scarica-${testid}`}><Download size={size} /></button>
    <button type="button" onClick={() => inviaFile(url, titolo, params)} title="Invia per email" className="p-1.5 rounded hover:bg-secondary text-sky-700 transition-colors" data-testid={`file-email-${testid}`}><Mail size={size} /></button>
  </span>
);

const tipoDi = (blob, nome) => {
  const t = blob?.type || "";
  const n = (nome || "").toLowerCase();
  if (t.includes("pdf") || n.endsWith(".pdf")) return "pdf";
  if (t.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic|bmp)$/.test(n)) return "img";
  return "altro";
};

const MailForm = ({ f, onClose }) => {
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const invia = async () => {
    if (!to.trim()) { toast.error("Scrivi l'indirizzo email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/files/invia-email", { url: f.url, params: f.params || null, nome: f.titolo, to, messaggio: msg });
      toast.success(`Email inviata a ${r.data.destinatari.join(", ")}`); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="file-mail-form">
      <div><label className="text-xs font-bold text-slate-500">Indirizzo email (anche più di uno, separati da virgola)</label>
        <Input type="text" inputMode="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="nome@esempio.it" className="mt-1" data-testid="file-mail-to" /></div>
      <div><label className="text-xs font-bold text-slate-500">Messaggio (facoltativo)</label>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} className="mt-1" placeholder="In allegato il documento richiesto." data-testid="file-mail-msg" /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} data-testid="file-mail-annulla">Annulla</Button>
        <Button onClick={invia} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="file-mail-invia">{busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Send size={15} className="mr-1" />} Invia con allegato</Button>
      </div>
    </div>
  );
};

export function FileViewerHost() {
  const [f, setF] = useState(null);
  const [mail, setMail] = useState(null);
  useEffect(() => {
    const on = async (e) => {
      const { url, titolo, params } = e.detail;
      setF({ url, titolo, params, loading: true });
      try {
        const res = await api.get(url, { params, responseType: "blob" });
        setF({ url, titolo, params, blob: res.data, obj: URL.createObjectURL(res.data), tipo: tipoDi(res.data, titolo) });
      } catch { setF(null); toast.error("Impossibile aprire il file"); }
    };
    const onMail = (e) => setMail(e.detail);
    window.addEventListener(EVT, on);
    window.addEventListener(EVT_MAIL, onMail);
    return () => { window.removeEventListener(EVT, on); window.removeEventListener(EVT_MAIL, onMail); };
  }, []);
  const chiudi = () => { if (f?.obj) URL.revokeObjectURL(f.obj); setF(null); };
  const scarica = () => { const a = document.createElement("a"); a.href = f.obj; a.download = f.titolo || "documento"; document.body.appendChild(a); a.click(); a.remove(); };
  return (
    <>
      <Dialog open={!!f} onOpenChange={(o) => { if (!o) chiudi(); }}>
        <DialogContent className="max-w-4xl w-[calc(100vw-1rem)] max-h-[92vh] p-3 sm:p-5 flex flex-col" data-testid="file-viewer">
          <DialogHeader className="pr-8">
            <DialogTitle className="flex items-center gap-2 text-base min-w-0"><FileText size={16} className="text-teal-600 shrink-0" /><span className="truncate" data-testid="file-viewer-titolo">{f?.titolo || "Documento"}</span></DialogTitle>
            <DialogDescription className="sr-only">Anteprima del documento</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden rounded-lg border bg-slate-100">
            {f?.loading && <div className="flex items-center justify-center py-16 text-muted-foreground gap-2"><Loader2 className="animate-spin" size={18} /> Caricamento…</div>}
            {f?.tipo === "pdf" && <PdfBlobViewer blob={f.blob} className="max-h-[70vh]" />}
            {f?.tipo === "img" && <div className="max-h-[70vh] overflow-auto p-2 grid place-items-center"><img src={f.obj} alt={f.titolo} className="max-w-full h-auto rounded" data-testid="file-viewer-img" /></div>}
            {f?.tipo === "altro" && <div className="py-12 text-center text-sm text-muted-foreground">Anteprima non disponibile per questo tipo di file. Usa «Scarica».</div>}
          </div>
          <div className="flex justify-end gap-2 pt-2 flex-wrap">
            <Button variant="outline" onClick={chiudi} data-testid="file-viewer-chiudi">Chiudi</Button>
            <Button variant="outline" onClick={() => { const m = { url: f.url, titolo: f.titolo, params: f.params }; chiudi(); setMail(m); }} disabled={!f?.obj} className="text-sky-700" data-testid="file-viewer-email"><Mail size={15} className="mr-1" /> Invia per email</Button>
            <Button onClick={scarica} disabled={!f?.obj} className="bg-teal-600 hover:bg-teal-700" data-testid="file-viewer-scarica"><Download size={15} className="mr-1" /> Scarica</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!mail} onOpenChange={(o) => { if (!o) setMail(null); }}>
        <DialogContent className="max-w-lg w-[calc(100vw-1rem)]" data-testid="file-mail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base min-w-0"><Mail size={16} className="text-sky-600 shrink-0" /><span className="truncate">Invia «{mail?.titolo || "documento"}» per email</span></DialogTitle>
            <DialogDescription>Il file arriva come allegato all'indirizzo che scrivi.</DialogDescription>
          </DialogHeader>
          {mail && <MailForm f={mail} onClose={() => setMail(null)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
